import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  LabelList,
  XAxis,
  YAxis,
  ZAxis,
  ReferenceLine,
  Tooltip,
  Legend,
  Label,
} from "recharts";
import { Button, ButtonGroup } from "rsuite";
import { setDate } from "rsuite/esm/internals/utils/date";
import { numberFormatterOnlyInM } from "../../../utils/formatter";

type TTState = {
  active: boolean;
  payload: any[];
  coordinate: { x: number; y: number } | null;
};

const GrowthVsTMScatterChart = ({
  activeIndex,
  setActiveIndex,
  commGrowthFilter,
  setCommGrowthFilter,
  salesGrowthFilter,
  setSalesGrowthFilter,
  tpGrowthFilter,
  setTpGrowthFilter,
  drill,
  setDrill,
  salesData,
  graphData,
  axisData,
  XYTicksData,
}: {
  activeIndex: string;
  setActiveIndex: React.Dispatch<React.SetStateAction<string>>;
  commGrowthFilter: string;
  setCommGrowthFilter: React.Dispatch<React.SetStateAction<string>>;
  salesGrowthFilter: string;
  setSalesGrowthFilter: React.Dispatch<React.SetStateAction<string>>;
  tpGrowthFilter: string;
  setTpGrowthFilter: React.Dispatch<React.SetStateAction<string>>;
  drill: "Up" | "Down";
  setDrill: React.Dispatch<React.SetStateAction<"Up" | "Down">>;
  salesData: Array<Record<string, string>>;
  graphData: Array<
    Record<string, string | number> & { x: number; y: number; z: number }
  >;
  axisData: { xSplit: number; ySplit: number };
  XYTicksData: { xTicks: number[]; yTicks: number[] };
}) => {
  const [data, setData] = useState<any>([]);
  const [commodityData, setCommodityData] = useState<
    Array<Record<string, string | number>>
  >([]);
  const [districtData, setDistrictData] = useState<
    Array<Record<string, string | number>>
  >([]);

  const commdityGraphTheme = {
    "Steel Conduit": "#50b539",
    "PVC Conduit": "#a02b93",
    "Copper Wire": "#0097a9",
  };

  const districtGraphTheme = {
    Atlanta: "#a0dbfe",
    Boston: "#e97132",
    California: "#da291c",
    Chicago: "#007cb0",
    Dallas: "#50b539",
    Minneapolis: "#9c765f",
    "New York": "#041e42",
    Pittsburgh: "#a02b93",
    Richmond: "#203c74",
    Seattle: "#ffc000",
    Southwest: "#62b5e5",
    "St. Louis": "#012169",
    Tampa: "#046A38",
    "district 14": "#8cd17e",
    "district 15": "#0097a9",
    "district 16": "#FF921D",
    "district 17": "#b17aa1",
    "district 18": "#bcbcbc",
  };

  const [xSplit, setXSplit] = useState<number>(1);
  const [ySplit, setYSplit] = useState<number>(1);

  const [xTicks, setXTicks] = useState<number[]>([]);
  const [yTicks, setYTicks] = useState<number[]>([]);

  const getMinMaX = () => {
    const gapAbs = (a: number, b: number) => Math.abs(b - a);
    if (data.length) {
      // const xs = data.map((d: any) => d.x);
      // const ys = data.map((d: any) => d.y);

      // setXSplit((Math.min(...xs) + Math.max(...xs)) / 2);
      // setYSplit((Math.min(...ys) + Math.max(...ys)) / 2);
      setXSplit(axisData.xSplit);
      setYSplit(axisData.ySplit);
      setXTicks(XYTicksData.xTicks);
      setYTicks(XYTicksData.yTicks);
    }
  };

  const xDomain = useMemo(() => {
    if (!data.length) return undefined;
    const xs = data.map((d: any) => Number(d.x)).filter(Number.isFinite);
    if (!xs.length) return undefined;
    const min = Math.min(...xs);
    const max = Math.max(...xs);
    const pad = (max - min) * 0.08 || 1; // fallback pad if all equal
    return [min - pad * 2, max + pad * 2] as [number, number];
  }, [data]);

  const yDomain = useMemo(() => {
    if (!data.length) return undefined;
    const ys = data.map((d: any) => Number(d.y)).filter(Number.isFinite);
    if (!ys.length) return undefined;
    const min = Math.min(...ys);
    const max = Math.max(...ys);
    const pad = (max - min) * 0.08 || 1;
    return [min - pad * 2, max + pad * 2] as [number, number];
  }, [data]);

  // useEffect(() => {
  //   if (drill === "Down") {
  //     const newCommData = graphData.map((ele) => {
  //       const commMin = [...graphData].sort((a, b) => a.z - b.z)[0].z;
  //       // const commAverage =
  //       //   commData.reduce((a, c) => a + c.z, 0) / commData.length;
  //       return { ...ele, size: ele.z / commMin };
  //     });
  //     setCommodityData(newCommData);
  //     setData(newCommData);
  //   } else {
  //     const newdisData = graphData.map((ele) => {
  //       const disMin = [...graphData].sort((a, b) => a.z - b.z)[0].z;
  //       // const disAverage =
  //       //   disData.reduce((a, c) => a + c.z, 0) / disData.length;
  //       return { ...ele, size: ele.z / disMin };
  //     });
  //     setDistrictData(newdisData);
  //     setData(newdisData);
  //   }
  // }, [drill]);
  useEffect(() => {
    if (!Array.isArray(graphData) || graphData.length === 0) {
      setData([]);
      if (drill === "Down") setCommodityData([]);
      else setDistrictData([]);
      return;
    }

    const minZ = graphData.reduce(
      (min, d) => (d.z < min ? d.z : min),
      Infinity,
    );
    const denom = minZ !== 0 ? minZ : 1; // avoids divide-by-zero / negative scaling

    const next = graphData.map((d) => ({
      ...d,
      size: d.z / denom,
    }));

    if (drill === "Down") setCommodityData(next);
    else setDistrictData(next);

    setData(next);
  }, [drill, graphData]);

  useEffect(() => {
    getMinMaX();
  }, [data]);

  const [tt, setTt] = useState<TTState>({
    active: false,
    payload: [],
    coordinate: null,
  });

  const [ttLocked, setTtLocked] = useState(false);
  const hideTimerRef = useRef<number | null>(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  };

  const scheduleHide = (ms = 120) => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      // only hide if user didn't enter tooltip
      if (!ttLocked) {
        setTt({ active: false, payload: [], coordinate: null });
      }
    }, ms);
  };

  const CustomTooltip = ({
    active,
    payload,
    coordinate,
    onEnter,
    onLeave,
  }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;

    return (
      <div
        className="bg-white border-1 border-[#ccc] p-2 rounded-md w-[13vw] flex flex-col gap-2 justify-between items-baseline shadow-2xl shadow-black/30"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <div className="w-full flex justify-center items-center text-deloitteGreen font-bold">
          {/* <div>{drill === "Down" ? "Commodity" : "District"} :</div> */}
          <div>{drill === "Down" ? p.commodity : p.district}</div>
        </div>
        <div className="w-full flex justify-between items-center">
          <span className="font-bold w-[64%]">TM %</span>
          <span className="font-bold w-[1%]">:</span>
          <span className="w-[35%] text-right">{p.y.toFixed(1)}%</span>
        </div>
        <div className="w-full flex justify-between items-center">
          <span className="font-bold w-[64%]">
            <span>YoY Sales</span>
            <span>
              {salesGrowthFilter === "Pounds(lbs)" ? " (lbs) " : " ($) "}
              Growth
            </span>
          </span>
          <span className="font-bold w-[1%]">:</span>
          <span className="w-[35%] text-right">{p.x.toFixed(1)}%</span>
        </div>
        <div className="w-full flex justify-between items-center">
          <span className="font-bold w-[64%]">
            Sales {salesGrowthFilter === "Pounds(lbs)" ? "(lbs)" : "($)"}
          </span>
          <span className="font-bold w-[1%]">:</span>
          <span className="w-[35%] text-right">
            {numberFormatterOnlyInM(
              p.z,
              1,
              salesGrowthFilter === "Pounds(lbs)" ? "" : "$",
            )}
          </span>
        </div>

        <div className="w-full flex justify-center items-center">
          <button
            className={` font-bold bg-deloitteGreen rounded-md text-white px-[0.4vw] py-[0.4vh]`}
            onClick={(e) => {
              e.stopPropagation();
              if (drill === "Down") {
                // setActiveIndex(p.commodity);
                setCommGrowthFilter(p.commodity);
              } else {
                // setActiveIndex("");
                setCommGrowthFilter("All");
              }
              setDrill(drill === "Up" ? "Down" : "Up");
              setActiveIndex("");
            }}
          >
            {drill === "Up" ? "Drill Up" : "Drill Down"}
          </button>
        </div>
      </div>
    );
  };

  const renderColouredLabel = (props: any) => {
    const { value, payload, viewBox } = props;
    const { x, y, width, height } = viewBox || {};
    const cx = (x ?? 0) + (width ?? 0) / 2;
    const cy = (y ?? 0) + (height ?? 0) / 2;

    const color =
      drill === "Up"
        ? districtGraphTheme[props.value as keyof typeof districtGraphTheme]
        : commdityGraphTheme[props.value as keyof typeof commdityGraphTheme];

    return (
      <text
        x={cx}
        y={cy}
        // dy={8}
        textAnchor="middle"
        // fill={color} // <-- same color as the dot
        fill={"#000"}
        fillOpacity={1} // <-- explicitly no opacity on text
        style={{ pointerEvents: "none", userSelect: "none" }}
        fontSize={12}
        fontWeight={600}
      >
        {value}
      </text>
    );
  };

  const CustomAxisLabel = ({ viewBox, value, angle = -90, axis }: any) => {
    const { x, y, width, height } = viewBox;
    const lines = value.split("\n");
    const cx = axis === "X" ? width / 2 : x;
    const cy = axis === "X" ? y + 40 : height / 2;
    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        transform={`rotate(${angle}, ${cx}, ${cy})`}
        fill="black"
        fontSize={"0.8vw"}
        fontWeight={600}
      >
        {lines.map((line: any, index: number) => (
          <tspan
            fill={index === 0 ? "black" : "#86BC25"}
            key={index}
            x={axis === "X" ? cx : 0}
            // y={axis === "X" ? 0 : cy}
            // dy={`${index * 1.3}em`}
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  const hasBoth = (a: number[]) =>
    a.some((n: number) => n > 0) && a.some((n: number) => n < 0);

  const formatAxisTick = (value: number) => {
    if (!Number.isFinite(value)) return "";
    return Math.round(value).toString();
  };

  return (
    <div
      className={`shadow-module-card bg-white h-full ${activeIndex !== "" ? "w-[65%]" : "w-full"} h-[77vh] py-[1vh] flex flex-col justify-between items-center`}
    >
      <div className="w-full px-[5vh] flex justify-between items-center h-[5%]">
        <div className="font-bold text-[2vh] w-[80%]">
          {/* <span className="text-[#86BC25]">{commGrowthFilter}</span> */}
          <span>
            {drill === "Down" ? (
              <span>
                <span className="text-[#86BC25]">All</span> Commodity
              </span>
            ) : (
              <span className="text-[#86BC25]">{commGrowthFilter}</span>
            )}{" "}
            Sales
          </span>
          <span className="text-[#86BC25]">
            {salesGrowthFilter === "Pounds(lbs)" ? " (lbs) " : " ($) "}
          </span>
          <span>Growth vs TM% </span>
          <span>{tpGrowthFilter === "YTD" ? "" : `for `}</span>
          <span className="text-[#86BC25]">{tpGrowthFilter}</span>
        </div>
        <div
          className={`font-bold text-[20%] text-right w-[18vw] h-[3.5vh] flex  ${drill === "Up" ? "justify-between" : "justify-end"}`}
        >
          {drill === "Up" && (
            <Button
              key={"drillUp"}
              active={true}
              size={"xs"}
              onClick={() => {
                setCommGrowthFilter("All");
                setDrill("Down");
                setActiveIndex("");
              }}
              className={`${"!bg-deloitteGreen !text-white !border-deloitteGreen w-[30%]"}`}
            >
              Drill Up
            </Button>
          )}
          <ButtonGroup className="rounded-md shadow-sm shadow-black/30 h-full w-[60%] flex justify-end">
            {salesData.map((ele: any, i: number) => {
              const isActive = ele.sales === salesGrowthFilter;
              return (
                <Button
                  key={ele.sales}
                  active={isActive}
                  size={"xs"}
                  onClick={() => setSalesGrowthFilter(ele.sales)}
                  className={`${i === 0 ? "w-[60%]" : "w-[40%]"} ${isActive ? "!bg-deloitteGreen !text-white !border-deloitteGreen" : ""}`}
                >
                  {ele.sales === "Pounds(lbs)" ? "Pounds" : ele.sales === "$ Value" ? "Dollars" : ele.sales}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>
      </div>
      {/* <div className="flex flex-wrap justify-start items-center h-[5%] w-full">
        {data?.map((ele: any, i: number) => (
          <div
            key={drill === "Down" ? ele.commodity : ele.district}
            className={`flex items-center ml-[2vh]`}
          >
            <div
              style={{
                backgroundColor:
                  drill === "Down"
                    ? commdityGraphTheme[
                        ele.commodity as keyof typeof commdityGraphTheme
                      ]
                    : districtGraphTheme[
                        ele.district as keyof typeof districtGraphTheme
                      ],
                opacity: "60%",
              }}
              className={`lengends h-[0.5vh] w-[1vw] rounded-[1vh] mr-[0.5vh]`}
            ></div>
            <div className="text-[1.4vh]">
              {drill === "Down" ? ele.commodity : ele.district}
            </div>
          </div>
        ))}
      </div> */}
      <div className={"w-full h-[95%]"}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            // className="pb-[4vh]"
            margin={{ top: 10, right: 10, bottom: 30, left: 10 }}
            style={{ padding: "1vh 1vh 1vh 1vh" }}
            onMouseLeave={() => {
              setTtLocked(false);
              scheduleHide(5);
            }}
          >
            <XAxis
              ticks={xTicks.length ? xTicks : undefined}
              domain={xDomain}
              dataKey="x"
              type="number"
              tickFormatter={formatAxisTick}
              name={`Sales (${salesGrowthFilter === "Pounds(lbs)" ? "lbs" : "$"}) Growth YoY`}
              unit="%"
            >
              <Label
                value={`Sales (${salesGrowthFilter === "Pounds(lbs)" ? "lbs" : "$"}) Growth YoY`}
                position="insideBottom"
                offset={-10}
                content={(props) => (
                  <CustomAxisLabel {...props} angle={0} axis={"X"} />
                )}
              />
            </XAxis>
            <YAxis
              ticks={yTicks.length ? yTicks : undefined}
              domain={yDomain}
              dataKey="y"
              type="number"
              tickFormatter={formatAxisTick}
              name="Transcation Margin %"
              unit="%"
            >
              <Label
                value="Transcation Margin %"
                angle={-90}
                position="left"
                offset={-10}
                content={(props) => (
                  <CustomAxisLabel {...props} angle={-90} axis={"Y"} />
                )}
              />
            </YAxis>
            <ZAxis
              dataKey="size"
              type="number"
              range={[1000, 6000]}
              name=""
              unit=""
            />
            <ReferenceLine
              x={xSplit}
              name="All Commodity Sales Growth"
              // position="end"
              stroke="#75787B"
              strokeWidth={2}
              strokeDasharray="6 6"
              ifOverflow="extendDomain"
            >
              <Label
                value={`${drill === "Down" ? "All Commodity" : `All GB ${commGrowthFilter}`}`}
                // angle={-90}
                position="insideTopRight"
                // offset={20}
                dx={-10}
                fill={"#75787B"}
                fontSize={"0.8vw"}
                fontWeight={600}
                fillOpacity={0.6}
              />
              <Label
                value={`Sales (${salesGrowthFilter === "Pounds(lbs)" ? "lbs" : "$"}) Growth: ${xSplit.toFixed(1)}%`}
                // angle={-90}
                position="insideTopRight"
                // offset={20}
                dx={-10}
                dy={20}
                // fill="#86BC25"
                fill={"#75787B"}
                fontSize={"0.8vw"}
                fontWeight={600}
                fillOpacity={0.6}
              />
            </ReferenceLine>
            <ReferenceLine
              y={ySplit}
              name="All Commodity TM%"
              stroke="#75787B"
              strokeWidth={2}
              strokeDasharray="6 6"
              ifOverflow="extendDomain"
            >
              <Label
                value={`${drill === "Down" ? "All Commodity" : `All GB ${commGrowthFilter}`}`}
                position="insideBottomRight"
                dy={-20}
                // fill={"#86BC25"}
                fill={"#75787B"}
                fontSize={"0.8vw"}
                fontWeight={600}
                fillOpacity={0.6}
              />
              <Label
                value={`TM%: ${ySplit.toFixed(1)}%`}
                position="insideBottomRight"
                // fill={"#86BC25"}
                fill={"#75787B"}
                fontSize={"0.8vw"}
                fontWeight={600}
                fillOpacity={0.6}
              />
            </ReferenceLine>
            {hasBoth(xTicks) && (
              <ReferenceLine
                x={0}
                name=""
                stroke="#BBBDBF"
                strokeWidth={1.5}
                strokeOpacity={0.65}
                strokeDasharray="4 6"
                ifOverflow="extendDomain"
              >
                <Label
                  value={"0%"}
                  position="insideBottom"
                  dy={16}
                  fill={"#75787B"}
                  fontSize={"0.8vw"}
                  fontWeight={600}
                  fillOpacity={0.6}
                />
              </ReferenceLine>
            )}

            {hasBoth(yTicks) && (
              <ReferenceLine
                y={0}
                name=""
                stroke="#BBBDBF"
                strokeWidth={1.5}
                strokeOpacity={0.65}
                strokeDasharray="4 6"
                ifOverflow="extendDomain"
              >
                {/* <Label
                  value={"0.0%"}
                  position="insideBottomRight"
                  // fill="#86BC25"
                  fill={"#75787B"}
                  fontSize={"0.8vw"}
                  fontWeight={600}
                  fillOpacity={0.6}
                /> */}
              </ReferenceLine>
            )}
            <Tooltip
              active={tt.active}
              payload={tt.payload}
              coordinate={tt.coordinate ?? undefined}
              wrapperStyle={{ pointerEvents: "auto" }}
              // trigger="click"
              cursor={{ strokeDasharray: "3 3" }}
              content={(props) => (
                <CustomTooltip
                  {...props}
                  onEnter={() => {
                    clearHideTimer();
                    setTtLocked(true);
                  }}
                  onLeave={() => {
                    setTtLocked(false);
                    scheduleHide(5);
                  }}
                />
              )}
            />
            <Scatter
              name="A school"
              data={data}
              fill="green"
              className="cursor-pointer"
              shape={(props: any) => {
                const { cx, cy, size, fill, payload } = props;
                // `size` is a scaled "area-like" value; convert to radius
                const r = Math.max(2, Math.sqrt((size ?? 0) / Math.PI));

                const isActive =
                  drill === "Down"
                    ? payload.commodity === activeIndex
                    : payload.district === activeIndex;
                return (
                  <g
                    className={`h-[${payload.height}] w-[${payload.width}]`}
                    onMouseEnter={() => {
                      if (ttLocked) return;
                      clearHideTimer();
                      setTt({
                        active: true,
                        payload: [{ payload }], // <-- matches your CustomTooltip usage
                        coordinate: { x: cx, y: cy },
                      });
                    }}
                    onMouseLeave={() => {
                      if (ttLocked) return;
                      scheduleHide(5);
                    }}
                    onClick={() => {
                      if (drill === "Up")
                        setActiveIndex(
                          activeIndex === payload.district
                            ? ""
                            : payload.district,
                        );
                      else {
                        setActiveIndex(
                          activeIndex === payload.commodity
                            ? ""
                            : payload.commodity,
                        );
                        if (drill === "Down")
                          setCommGrowthFilter(
                            activeIndex === payload.commodity
                              ? "All"
                              : payload.commodity,
                          );
                      }
                    }}
                  >
                    <circle
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill={`${
                        drill === "Up"
                          ? districtGraphTheme[
                              payload.district as keyof typeof districtGraphTheme
                            ]
                          : commdityGraphTheme[
                              payload.commodity as keyof typeof commdityGraphTheme
                            ]
                      }`}
                      fillOpacity={0.6}
                      stroke={
                        !isActive
                          ? "none"
                          : // : `${districtGraphTheme[payload.district as keyof typeof districtGraphTheme]}`
                            "black"
                      }
                      strokeWidth={isActive ? 2 : 0}
                      strokeOpacity={0.8}
                      style={{
                        cursor: "pointer",
                        // filter: "drop-shadow(0px 6px 10px rgba(0,0,0,0.30))",
                      }}
                    />
                  </g>
                );
              }}
            >
              <LabelList
                dataKey={drill === "Up" ? "district" : "commodity"}
                // fill="black"
                style={{ pointerEvents: "none", userSelect: "none" }}
                content={renderColouredLabel}
              />
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GrowthVsTMScatterChart;
